'use client';

import studioStyles from '@/components/reference/CreationDialog.module.css';

import { Check, ImagePlus, Loader2, Package, Upload, UserRound, X } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { IMAGE_FILE_INPUT_ACCEPT, materialsApi, type UserMaterial } from '@/lib/api/materials';
import type { VideoReplicationReferenceRole } from '@/lib/api/video-replications';
import { toast } from '@/lib/stores/toast-store';
import { useDialogFocus } from '@/lib/hooks/useDialogFocus';
import { ShootingMedia } from '@/components/shooting/ShootingMedia';
import { kagemaneSubjectReference } from '@/lib/kagemane-subject-reference';
import type { SubjectModelType } from '@/types/subject-model';

import { KagemaneReferenceEditor, type ReferenceEditorSource } from './KagemaneReferenceEditor';
import { KagemaneVideoThumbnail } from './KagemaneVideoThumbnail';

type MediaKind = 'image' | 'video';
const RECENT_LIMIT = 24;
const IMAGE_ROLES = ['auto', 'object', 'person', 'garment', 'outfit', 'scene'] as const;
const UserReferencePickerModal = dynamic(() => import('@/components/reference/UserReferencePickerModal').then(module => module.UserReferencePickerModal), { ssr: false });
const KagemanePortraitPicker = dynamic(() => import('./KagemanePortraitPicker').then(module => module.KagemanePortraitPicker), { ssr: false });

/** Preserve the server order and explicitly prepend completed local uploads. */
function recentMaterials(items: UserMaterial[]): UserMaterial[] {
  const seen = new Set<string>();
  return items.filter(item => {
    // Camera stills are intermediate inputs; present the finished reference sheet.
    if (item.tags?.includes('kagemane-camera-frame')) return false;
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export function KagemaneMediaPicker({ kind, recentScope = 'kagemane', authenticated, onRequireLogin, onClose, onSelect, assignments = {}, maxAssignments = 16, maxVideoDurationMs = 30000, onAssign, onRemove }: {
  kind: MediaKind;
  recentScope?: 'kagemane' | 'all';
  authenticated: boolean;
  onRequireLogin: () => void;
  onClose: () => void;
  onSelect: (material: UserMaterial) => void;
  assignments?: Record<string, VideoReplicationReferenceRole>;
  maxAssignments?: number;
  maxVideoDurationMs?: number;
  onAssign?: (material: UserMaterial, role: VideoReplicationReferenceRole) => void;
  onRemove?: (id: string) => void;
}) {
  const t = useTranslations('kagemane');
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const referenceAnchorRef = useRef<HTMLElement>(null);
  const [referencePicker, setReferencePicker] = useState<SubjectModelType | null>(null);
  const requestRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const nextOffsetRef = useRef(0);
  const uploadingRef = useRef(false);
  const uploadRole = useRef<VideoReplicationReferenceRole>('auto');
  const lastUploadRole = useRef<VideoReplicationReferenceRole>('auto');
  const selectedIdsRef = useRef(new Set(Object.keys(assignments)));
  const [materials, setMaterials] = useState<UserMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set(Object.keys(assignments)));
  const selectedMaterials = useRef(new Map<string, UserMaterial>());
  const selectedRoles = useRef(new Map<string, VideoReplicationReferenceRole>());
  const [uploads, setUploads] = useState<Array<{ file: File; progress: number; status: 'uploading' | 'ready' | 'failed' }>>([]);
  const [error, setError] = useState(false);
  const [videoSource, setVideoSource] = useState<ReferenceEditorSource | null>(null);
  const assignedCount = selectedIds.size;
  const imageLimit = Math.min(maxAssignments, 16);


  useDialogFocus(dialogRef, !referencePicker && !videoSource, () => { if (!uploadingRef.current) onClose(); });
  useEffect(() => {
    if (referencePicker || !referenceAnchorRef.current) return;
    const frame = requestAnimationFrame(() => referenceAnchorRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [referencePicker]);

  const load = async (append = false) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const request = ++requestRef.current;
    const offset = append ? nextOffsetRef.current : 0;
    if (append) setLoadingMore(true); else setLoading(true);
    setError(false);
    try {
      const result = await materialsApi.getMaterials(
        { file_type: kind, asset_category: 'all', usage_scope: recentScope === 'kagemane' ? 'kagemane' : undefined, limit: RECENT_LIMIT, offset },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (!result.success || !result.data) throw result.error ?? new Error(t('mediaLoadFailed'));
      if (request === requestRef.current) {
        const items = result.data.items;
        // Uploads may finish while this page is in flight. Never replace them.
        setMaterials(current => recentMaterials([...current, ...items.filter(item => item.file_type === kind)]));
        nextOffsetRef.current = offset + items.length;
        setHasMore(items.length > 0 && nextOffsetRef.current < result.data.total);
      }
    } catch (cause) {
      if (!controller.signal.aborted && request === requestRef.current) {
        setError(true);
        toast.showError(cause, 'asset', { fallbackMessage: t('mediaLoadFailed') });
      }
    } finally {
      if (request === requestRef.current) { setLoading(false); setLoadingMore(false); }
    }
  };

  useEffect(() => {
    nextOffsetRef.current = 0;
    setHasMore(false);
    setMaterials([]);
    if (!authenticated) { setMaterials([]); setLoading(false); setError(false); return; }
    if (uploadingRef.current) return;
    void load();
    return () => { requestRef.current += 1; abortRef.current?.abort(); };
  }, [kind, authenticated, recentScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSelection = () => setSelectedIds(new Set(selectedIdsRef.current));

  const upload = async (files: FileList | File[] | null, role = uploadRole.current) => {
    if (!authenticated) { onRequireLogin(); return; }
    if (!files?.length || uploadingRef.current) return;
    if (kind === 'video') {
      setVideoSource({ file: files[0] });
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    const slots = imageLimit - selectedIdsRef.current.size;
    if (slots <= 0) { toast.warning(t('materialLimit', { count: imageLimit })); return; }
    if (files.length > slots) toast.warning(t('materialLimit', { count: imageLimit }));
    const batch = Array.from(files).slice(0, slots);
    lastUploadRole.current = role;
    uploadRole.current = 'auto';
    uploadingRef.current = true;
    setError(false);
    setUploading(true);
    setUploads(batch.map((file) => ({ file, progress: 0, status: 'uploading' })));
    let nextIndex = 0;
    const uploaded: Array<UserMaterial | undefined> = new Array(batch.length);
    const updateUpload = (index: number, update: Partial<(typeof uploads)[number]>) => {
      setUploads((current) => current.map((entry, i) => i === index ? { ...entry, ...update } : entry));
    };
    const worker = async () => {
      while (nextIndex < batch.length) {
        const index = nextIndex++;
        const file = batch[index];
        try {
          const result = await materialsApi.uploadMaterial(file, 'general', {
            displayName: file.name.replace(/\.[^/.]+$/, ''),
            tags: recentScope === 'kagemane' ? ['kagemane'] : undefined,
            onProgress: (progress) => updateUpload(index, { progress }),
          });
          if (!result.success || !result.data?.id || result.data.file_type !== kind || !result.data.url) {
            throw result.error ?? new Error(t('mediaUploadFailed'));
          }
          const item = result.data as UserMaterial;
          uploaded[index] = item;
          selectedMaterials.current.set(item.id, item);
          if (role !== 'auto') selectedRoles.current.set(item.id, role);
          setMaterials((current) => recentMaterials([item, ...current.filter((entry) => entry.id !== item.id)]));
          updateUpload(index, { progress: 100, status: 'ready' });
        } catch {
          updateUpload(index, { status: 'failed' });
        }
      }
    };
    try {
      // Bound simultaneous transfers while showing each file as soon as it finishes.
      await Promise.all(Array.from({ length: Math.min(3, batch.length) }, () => worker()));
      // File selection order defines image numbering, never network completion order.
      for (const item of uploaded) if (item) selectedIdsRef.current.add(item.id);
      // Keep the batch visible in file order, including re-uploads of old assets.
      setMaterials(current => recentMaterials([...uploaded.filter((item): item is UserMaterial => Boolean(item)), ...current]));
      updateSelection();
      await rememberMaterials(uploaded.filter((item): item is UserMaterial => Boolean(item)));
    } finally {
      uploadingRef.current = false;
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const toggleImage = (item: UserMaterial) => {
    if (uploadingRef.current) return;
    if (!authenticated) { onRequireLogin(); return; }
    if (selectedIdsRef.current.has(item.id)) {
      selectedIdsRef.current.delete(item.id);
    } else if (selectedIdsRef.current.size >= imageLimit) {
      toast.warning(t('materialLimit', { count: imageLimit }));
    } else {
      selectedIdsRef.current.add(item.id);
      selectedMaterials.current.set(item.id, item);
    }
    updateSelection();
  };

  const rememberMaterials = async (items: UserMaterial[]) => {
    if (recentScope !== 'kagemane') return;
    const ids = [...new Set(items.map(item => item.id))]
      .filter(id => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id));
    if (!ids.length) return;
    try {
      const result = await materialsApi.rememberKagemaneMaterials(ids);
      if (!result.success) throw result.error ?? new Error(t('mediaLoadFailed'));
    } catch (cause) {
      // A failed Recent write must not discard an uploaded or chosen reference.
      toast.showError(cause, 'asset', { fallbackMessage: t('mediaLoadFailed') });
    }
  };
  const confirmImages = async () => {
    if (uploadingRef.current) return;
    const chosen = [...selectedIdsRef.current].flatMap(id => {
      const item = selectedMaterials.current.get(id);
      return item ? [item] : [];
    });
    if (recentScope === 'kagemane' && chosen.some(item => /^[0-9a-f-]{36}$/i.test(item.id))) {
      uploadingRef.current = true;
      setUploading(true);
      try { await rememberMaterials(chosen); }
      finally { uploadingRef.current = false; setUploading(false); }
    }
    for (const id of Object.keys(assignments)) {
      if (!selectedIdsRef.current.has(id)) onRemove?.(id);
    }
    for (const id of selectedIdsRef.current) {
      const item = selectedMaterials.current.get(id);
      if (item && (!assignments[id] || selectedRoles.current.has(id))) onAssign?.(item, selectedRoles.current.get(id) ?? 'auto');
    }
    onClose();
  };
  const setImageRole = (item: UserMaterial, role: VideoReplicationReferenceRole) => {
    if (uploadingRef.current || !selectedIdsRef.current.has(item.id)) return;
    selectedMaterials.current.set(item.id, item);
    selectedRoles.current.set(item.id, role);
    updateSelection();
  };
  const addReference = (item: UserMaterial, role: VideoReplicationReferenceRole): boolean => {
    if (uploadingRef.current) return false;
    if (!authenticated) { onRequireLogin(); return false; }
    if (!selectedIdsRef.current.has(item.id) && selectedIdsRef.current.size >= imageLimit) {
      toast.warning(t('materialLimit', { count: imageLimit }));
      return false;
    }
    selectedIdsRef.current.add(item.id);
    selectedMaterials.current.set(item.id, item);
    selectedRoles.current.set(item.id, role);
    setMaterials(current => recentMaterials([item, ...current]));
    updateSelection();
    return true;
  };
  const failedUploads = uploads.filter((entry) => entry.status === 'failed');
  const completedUploads = uploads.filter((entry) => entry.status !== 'uploading').length;

  const focus = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] focus-visible:ring-offset-2';

  return <>{createPortal(
    <div className="fixed inset-0 z-[230] flex items-center justify-center bg-[#172B36]/35 p-3 sm:p-6" onMouseDown={(event) => { if (!referencePicker && !uploadingRef.current && event.target === event.currentTarget) onClose(); }}>
      <section ref={dialogRef} tabIndex={-1} role="dialog" aria-modal={!referencePicker} inert={Boolean(referencePicker)} aria-busy={uploading} aria-label={t(kind === 'video' ? 'pickVideo' : 'pickImage')}
        className={`${studioStyles.dialog} flex h-[min(640px,85dvh)] w-full min-w-0 max-w-3xl flex-col overflow-hidden rounded-2xl border border-[#E5E9EC] bg-white text-[#1B242B] shadow-2xl outline-none`}>
        <header className="flex shrink-0 items-center justify-between gap-2 px-4 py-3">
          <h2 className="text-base font-semibold">{t(kind === 'video' ? 'pickVideo' : 'pickImage')}</h2>
          <button type="button" onClick={onClose} disabled={uploading} aria-label={t('close')}
            className={`grid size-10 shrink-0 place-items-center rounded-full text-[#53636D] hover:bg-[#F3F5F6] disabled:opacity-40 ${focus}`}><X className="size-5" aria-hidden /></button>
        </header>
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-[#E5E9EC] px-4 pb-3">
          <span className={`${kind === 'image' ? 'sr-only sm:not-sr-only' : ''} text-sm font-medium text-[#1B242B]`}>{t('recentMedia')}</span>
          <div className={`flex flex-wrap items-center gap-1 ${kind === 'image' ? 'w-full justify-between sm:w-auto sm:justify-end' : 'justify-end'}`}>
          {kind === 'image' && (['product', 'character'] as const).map(type => {
            const Icon = type === 'character' ? UserRound : Package;
            return <button key={type} type="button" disabled={uploading}
              aria-haspopup="dialog" aria-expanded={referencePicker === type}
              aria-label={t(type === 'character' ? 'portraitLibrary' : 'productLibrary')}
              onClick={event => {
                if (!authenticated) { onRequireLogin(); return; }
                referenceAnchorRef.current = event.currentTarget;
                setReferencePicker(type);
              }}
              className={`inline-flex min-h-10 items-center gap-1.5 rounded-lg px-2 text-xs text-[#53636D] transition-colors hover:bg-[#F3F5F6] hover:text-[#1B242B] disabled:opacity-50 sm:px-2.5 sm:text-sm ${focus}`}>
              <Icon className="size-4" aria-hidden /><span>{t(type === 'character' ? 'portraitLibrary' : 'productLibrary')}</span>
            </button>;
          })}
          <button type="button" onClick={() => {uploadRole.current = 'auto'; if (authenticated) inputRef.current?.click(); else onRequireLogin();}} disabled={uploading || (authenticated && kind === 'image' && assignedCount >= imageLimit)}
            className={`flex min-h-11 items-center justify-center gap-2 rounded-full bg-[#4ABDE9] px-4 text-sm font-medium text-[#102A38] hover:bg-[#6DCAEE] disabled:opacity-50 ${focus}`}>
            {uploading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Upload className="size-4" aria-hidden />}
            <span className={kind === 'image' ? 'sr-only sm:not-sr-only' : undefined}>{t(kind === 'image' ? 'uploadImages' : 'uploadMedia')}</span>
          </button>
          </div>
        </div>
        <div role="region" aria-label={t('recentMedia')} aria-busy={loading || loadingMore || uploading}
          className="flex min-h-0 flex-1 flex-col bg-[#F7F9FA] pt-3">
          {kind === 'image' && <p className="mx-4 mb-3 shrink-0 text-xs leading-5 text-[#53636D]">{t('materialRoleHint')}</p>}
          {uploads.length > 0 && <div className="mx-4 mb-3 max-h-32 shrink-0 overflow-y-auto rounded-lg border border-[#DEE4E8] bg-white px-3 py-2 text-xs">
            <p role="status" className="mb-1 font-medium">{t(uploading ? 'batchUploading' : 'batchUploaded', { completed: completedUploads - failedUploads.length, total: uploads.length })}</p>
            {uploads.filter((entry) => entry.status !== 'ready').map((entry, index) => <div key={index} className="flex items-center gap-2 py-1">
              <span className="min-w-0 flex-1 truncate" title={entry.file.name}>{entry.file.name}</span>
              <span className={entry.status === 'failed' ? 'text-red-600' : 'text-[#69767D]'}>{entry.status === 'failed' ? t('uploadFailed') : `${Math.round(entry.progress)}%`}</span>
            </div>)}
            {failedUploads.length > 0 && !uploading && <button type="button" onClick={() => void upload(failedUploads.map((entry) => entry.file), lastUploadRole.current)} className={`mt-1 min-h-9 rounded px-2 font-medium text-[#176B89] ${focus}`}>{t('retryFailedUploads')}</button>}
          </div>}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4">
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 sm:gap-3 md:grid-cols-6">
              {authenticated && materials.map((item, index) => <div key={item.id} className="relative min-w-0"><button type="button" onClick={() => kind === 'image' ? toggleImage(item) : setVideoSource({ material: item })} disabled={uploading}
                aria-label={t(kind === 'video' ? 'selectRecentVideo' : 'selectRecentImage', { number: index + 1 })} aria-pressed={kind === 'image' ? selectedIds.has(item.id) : undefined}
                className={`group block w-full min-w-0 overflow-hidden rounded-lg border-2 bg-white text-left disabled:opacity-60 ${focus} ${selectedIds.has(item.id) ? 'border-[#4ABDE9]' : 'border-transparent hover:border-[#CBD3D9]'}`}>
                <span className="relative block aspect-square overflow-hidden bg-[#E9EDF0]">
                  {kind === 'video' ? <KagemaneVideoThumbnail src={item.url} poster={item.thumbnail_url} />
                    : item.thumbnail_url || item.url ? <ShootingMedia assetId={item.id} src={item.thumbnail_url || item.url} variant="thumbnail" alt="" className="h-full w-full" />
                    : <span className="grid h-full place-items-center text-[#819098]"><ImagePlus aria-hidden /></span>}
                  {selectedIds.has(item.id) && <span className="absolute right-1 top-1 grid size-5 place-items-center rounded-full bg-[#4ABDE9] text-[#102A38]"><Check className="size-3.5" aria-hidden /></span>}
                </span>
              </button>
                {kind === 'image' && selectedIds.has(item.id) && <select
                  aria-label={t('materialRoleForImage', {number: index + 1})}
                  value={selectedRoles.current.get(item.id) ?? assignments[item.id] ?? 'auto'}
                  disabled={uploading}
                  onChange={event => setImageRole(item, event.target.value as VideoReplicationReferenceRole)}
                  className={`absolute bottom-1 left-1 h-8 w-[calc(100%-8px)] cursor-pointer rounded-md border border-[#DEE4E8] bg-white px-1.5 text-xs font-medium text-[#203644] shadow-sm disabled:opacity-50 ${focus}`}>
                  {IMAGE_ROLES.map(role => <option key={role} value={role}>{t(`materialRole_${role}`)}</option>)}
                </select>}
              </div>)}
            </div>
            {loading && <p role="status" className="mt-10 text-center text-sm text-[#69767D]">{t('loadingMedia')}</p>}
            {error && <div role="alert" className="mt-10 text-center"><p className="text-sm text-[#69767D]">{t('mediaLoadFailed')}</p><button type="button" onClick={() => void load(nextOffsetRef.current > 0)} disabled={uploading} className={`mt-2 min-h-11 rounded-lg px-4 text-sm text-[#176B89] disabled:opacity-50 ${focus}`}>{t('retryMedia')}</button></div>}
            {authenticated && hasMore && !error && <div className="mt-4 text-center"><button type="button" onClick={() => void load(true)} disabled={loading || loadingMore || uploading} className={`min-h-11 rounded-lg px-4 text-sm text-[#176B89] disabled:opacity-50 ${focus}`}>{t(loadingMore ? 'loadingMedia' : 'loadMoreMedia')}</button></div>}
            {!authenticated && <p className="mt-6 text-sm text-[#69767D]">{t('signInToUpload')}</p>}
            {authenticated && !loading && !error && materials.length === 0 && <p className="mt-10 text-center text-sm text-[#69767D]">{t('noMedia')}</p>}
          </div>
        </div>
        <footer className={`${studioStyles.footer} flex shrink-0 items-center justify-between gap-3 border-t border-[#E5E9EC] px-4 py-3`}>
          <span aria-live="polite" className="text-xs text-[#69767D]">{kind === 'image' ? t('imageSelectionCount', { count: assignedCount, limit: imageLimit }) : t('selectVideoHint')}</span>
          <button type="button" disabled={uploading} onClick={kind === 'image' ? confirmImages : onClose}
            className={`min-h-11 shrink-0 rounded-full bg-[#4ABDE9] px-6 text-sm font-medium text-[#102A38] hover:bg-[#6DCAEE] disabled:opacity-50 ${focus}`}>{t(kind === 'image' ? 'confirmSelection' : 'close')}</button>
        </footer>
        <input ref={inputRef} type="file" accept={kind === 'video' ? 'video/*' : IMAGE_FILE_INPUT_ACCEPT} multiple={kind === 'image'} className="hidden" onChange={(event) => void upload(event.target.files)} />
      </section>
    </div>,
    document.body,
  )}{referencePicker === 'character' ? <KagemanePortraitPicker anchorRef={referenceAnchorRef} selectedIds={selectedIds} onClose={() => setReferencePicker(null)} onSelect={material => addReference(material, 'person')} /> : referencePicker && <UserReferencePickerModal open modal appearance="solid" filter={referencePicker} anchorRef={referenceAnchorRef}
    onClose={() => setReferencePicker(null)}
    onUploadFromDevice={() => { uploadRole.current = 'object'; setReferencePicker(null); inputRef.current?.click(); }}
    onSelectMaterial={material => addReference(material, 'object')}
    onSelectSubjectModel={model => {
      const material = kagemaneSubjectReference(model);
      if (!material) { toast.error(t('subjectReferenceUnavailable')); return false; }
      return addReference(material, model.model_type === 'character' ? 'person' : 'object');
    }}
    onSelectCustomStyle={() => {}} />}
    {videoSource && <KagemaneReferenceEditor source={videoSource} maxDurationMs={maxVideoDurationMs} onClose={() => setVideoSource(null)} onConfirm={onSelect} />}</>;
}
